/**
 * Karta príspevku/vlákna (S11, F4AI-17) — jednotné zobrazenie
 * `EventSummary` z gateway (feed, topic, search, reply tree).
 */
import { type JSX } from 'react';
import { Link } from 'react-router';

import type { EventSummary, ReplyNode } from '../api/types.js';
import {
  addressFromTags,
  bodyFromContent,
  formatAbsoluteTime,
  formatRelativeTime,
  kindClassName,
  kindLabel,
  titleFromContent,
  topicTags,
} from '../lib/format.js';
import { npubOf, npubShort } from '../lib/npub.js';

export function EventCard({
  event,
  to,
  testId = 'event-card',
  untrusted = false,
}: {
  event: EventSummary;
  to?: string;
  testId?: string;
  /** F4AI-18: obsah fóra nie je moderovaný — badge na kartách vo vlákne */
  untrusted?: boolean;
}): JSX.Element {
  const target = to ?? `/threads/${event.id}`;
  const tags = topicTags(event.tags);
  const topic = addressFromTags(event.tags);

  return (
    <article className="glass-card event-card" data-testid={testId}>
      <div className="event-meta">
        <span className={`kind-badge ${kindClassName(event.kind)}`}>{kindLabel(event.kind)}</span>
        {untrusted && (
          <span className="untrusted-badge" data-testid="untrusted-badge" title="obsah neprináleží moderátorom fóra">
            untrusted
          </span>
        )}
        <time dateTime={new Date(event.created_at * 1000).toISOString()} title={formatAbsoluteTime(event.created_at)}>
          {formatRelativeTime(event.created_at)}
        </time>
        <Link
          className="pubkey-chip"
          to={`/agents/${event.pubkey}`}
          title={npubOf(event.pubkey)}
          data-testid="event-author"
        >
          {npubShort(event.pubkey)}
        </Link>
      </div>

      <h3>
        <Link to={target}>{titleFromContent(event.content)}</Link>
      </h3>
      <p className="event-body">{bodyFromContent(event.content)}</p>

      {(tags.length > 0 || topic !== null) && (
        <div className="event-meta">
          {topic !== null && <span className="tag-chip">topic: {topic.split(':')[2] ?? '?'}</span>}
          {tags.map((tag) => (
            <span key={tag} className="tag-chip">
              #{tag}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}

/** rekurzívny strom odpovedí z `GET /threads/:id` (`ReplyNode`) */
export function ReplyTree({
  nodes,
  depth = 0,
  untrusted = true,
}: {
  nodes: ReplyNode[];
  depth?: number;
  untrusted?: boolean;
}): JSX.Element {
  return (
    <div className="reply-tree" data-depth={depth} data-testid={`reply-tree-${String(depth)}`}>
      {nodes.map((node) => (
        <div key={node.id} className="reply-node" data-testid={`reply-node-${node.id.slice(0, 8)}`}>
          <EventCard event={node} to={`/threads/${node.id}`} testId="reply-card" untrusted={untrusted} />
          {node.children.length > 0 && <ReplyTree nodes={node.children} depth={depth + 1} untrusted={untrusted} />}
        </div>
      ))}
    </div>
  );
}
